import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { euro, formatDate } from "@/lib/constants";
import { Car, Plus, Pencil, Fuel, CreditCard, ChevronLeft, ChevronRight, AlertTriangle, Upload } from "lucide-react";
import { BulkImportDialog } from "@/components/BulkImportDialog";

const EMPTY = { modello: "", targa: "", disponibile: true, carta_carburante: false, telepass: false, parcheggio_base: "", note: "" };
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function pad(n) { return String(n).padStart(2, "0"); }

export default function CompanyCars() {
  const { user, activeCompany } = useAuth();
  const [cars, setCars] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [monthDate, setMonthDate] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const effectiveCompany = activeCompany || user?.active_company || user?.company;
  const isReadOnly = effectiveCompany === "TUTTE";
  const isAdmin = user?.role === "amministratore";

  const load = useCallback(() => api.get("/company-cars").then((r) => setCars(r.data)), []);
  const loadSchedule = useCallback(() => api.get("/company-cars/schedule").then((r) => setAssignments(r.data.assignments || [])), []);
  useEffect(() => { load(); loadSchedule(); }, [load, loadSchedule]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm(c); setEditId(c.id); setOpen(true); };

  const save = async () => {
    try {
      if (editId) await api.put(`/company-cars/${editId}`, form);
      else await api.post("/company-cars", form);
      toast.success("Auto salvata"); setOpen(false); load(); loadSchedule();
    } catch (e) { toast.error(apiError(e)); }
  };

  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const carMatches = (a, c) => (a.targa && a.targa === c.targa) || (a.modello && a.modello === c.modello);

  // occupancy[carId][day] = count
  const occupancy = useMemo(() => {
    const map = {};
    cars.forEach((c) => { map[c.id] = {}; });
    assignments.forEach((a) => {
      const car = cars.find((c) => carMatches(a, c));
      if (!car || !a.data_inizio) return;
      const start = new Date(a.data_inizio);
      const end = new Date(a.data_fine || a.data_inizio);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        if (d.getFullYear() === year && d.getMonth() === month) {
          const day = d.getDate();
          map[car.id][day] = (map[car.id][day] || 0) + 1;
        }
      }
    });
    return map;
  }, [cars, assignments, year, month]);

  const monthAssignments = assignments.filter((a) => {
    if (!a.data_inizio) return false;
    const s = new Date(a.data_inizio), e = new Date(a.data_fine || a.data_inizio);
    const mStart = new Date(year, month, 1), mEnd = new Date(year, month + 1, 0);
    return s <= mEnd && e >= mStart;
  });

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Auto aziendali</h1>
          <p className="text-slate-500 mt-1">Flotta {isReadOnly ? "consolidata" : effectiveCompany} ({cars.length} veicoli) e calendario disponibilità.</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && <BulkImportDialog entity="company-cars" disabled={isReadOnly} onComplete={load} trigger={<Button data-testid="import-cars-btn" disabled={isReadOnly} variant="outline"><Upload size={17} className="mr-1.5" /> Importa veicoli</Button>} />}
          {!isReadOnly && <Button data-testid="add-car-btn" onClick={openNew} className="bg-[#E30613] hover:bg-[#C50510] font-semibold"><Plus size={18} className="mr-1.5" /> Nuova auto</Button>}
        </div>
      </div>

      <Tabs defaultValue="flotta">
        <TabsList className="mb-6">
          <TabsTrigger value="flotta" data-testid="tab-flotta"><Car size={15} className="mr-1.5" /> Flotta</TabsTrigger>
          <TabsTrigger value="calendario" data-testid="tab-calendario">Calendario disponibilità</TabsTrigger>
        </TabsList>

        <TabsContent value="flotta">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {cars.map((c) => (
              <div key={c.id} data-testid={`car-${c.id}`} className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="h-11 w-11 rounded-lg bg-slate-900 text-white flex items-center justify-center"><Car size={22} /></div>
                  <span className={`text-xs font-semibold rounded-full px-2.5 py-0.5 border ${c.disponibile ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-rose-50 text-rose-700 border-rose-300"}`}>
                    {c.disponibile ? "Disponibile" : "Non disponibile"}
                  </span>
                </div>
                <h3 className="font-display text-lg font-semibold mt-3">{c.modello}</h3>
                <div className="font-mono text-sm text-slate-500">{c.targa}</div>
                <div className="flex gap-3 mt-3 text-xs text-slate-500">
                  {c.carta_carburante && <span className="flex items-center gap-1"><Fuel size={13} /> Carta carburante</span>}
                  {c.telepass && <span className="flex items-center gap-1"><CreditCard size={13} /> Telepass</span>}
                </div>
                {c.parcheggio_base && <div className="text-xs text-slate-400 mt-2">Base: {c.parcheggio_base}</div>}
                {!isReadOnly && <Button variant="outline" size="sm" className="mt-4 w-full" data-testid={`edit-car-${c.id}`} onClick={() => openEdit(c)}><Pencil size={14} className="mr-1.5" /> Modifica</Button>}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="calendario">
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 sm:p-6" data-testid="cars-calendar">
            <div className="flex items-center justify-between mb-4">
              <button data-testid="cal-prev" onClick={() => setMonthDate(new Date(year, month - 1, 1))} className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50"><ChevronLeft size={16} /></button>
              <div className="font-display text-lg font-semibold">{MESI[month]} {year}</div>
              <button data-testid="cal-next" onClick={() => setMonthDate(new Date(year, month + 1, 1))} className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50"><ChevronRight size={16} /></button>
            </div>

            <div className="overflow-x-auto">
              <table className="border-collapse text-xs">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-white text-left px-2 py-2 min-w-[140px] font-medium text-slate-500">Auto</th>
                    {days.map((d) => {
                      const dow = new Date(year, month, d).getDay();
                      const weekend = dow === 0 || dow === 6;
                      return <th key={d} className={`px-0 py-1 w-7 text-center font-medium ${weekend ? "text-[#E30613]" : "text-slate-400"}`}>{d}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {cars.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100">
                      <td className="sticky left-0 bg-white px-2 py-2 font-medium text-slate-800 whitespace-nowrap">
                        {c.modello}<div className="font-mono text-[10px] text-slate-400">{c.targa}</div>
                      </td>
                      {days.map((d) => {
                        const count = occupancy[c.id]?.[d] || 0;
                        const conflict = count > 1;
                        return (
                          <td key={d} className="px-0.5 py-1 text-center" data-testid={count ? `cal-${c.id}-${d}` : undefined}>
                            <div className={`h-6 w-6 mx-auto rounded ${conflict ? "bg-amber-400 ring-2 ring-rose-500" : count ? "bg-[#E30613]" : "bg-slate-50 border border-slate-100"}`} title={count ? `${count} assegnazione/i` : "Libera"} />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center gap-4 mt-4 text-xs text-slate-500 flex-wrap">
              <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-[#E30613]" /> Occupata</span>
              <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-slate-50 border border-slate-200" /> Libera</span>
              <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-amber-400 ring-2 ring-rose-500" /> Sovrapposizione</span>
            </div>

            <div className="mt-6">
              <h4 className="font-semibold text-sm mb-3">Assegnazioni del mese</h4>
              {monthAssignments.length === 0 ? (
                <p className="text-sm text-slate-400">Nessuna assegnazione in {MESI[month]}.</p>
              ) : (
                <div className="space-y-2">
                  {monthAssignments.map((a, i) => (
                    <div key={a.trip_id || `${a.targa || a.modello}-${a.data_inizio}-${a.data_fine}`} className="flex items-center justify-between text-sm border border-slate-100 rounded-lg px-3 py-2" data-testid={`assignment-${a.trip_id || `${a.targa || a.modello}-${a.data_inizio}`}`}>
                      <span className="font-medium text-slate-800">{a.modello || a.targa || "Auto"} <span className="text-slate-400 font-normal">· {a.destinazione} ({a.dipendente})</span></span>
                      <span className="text-slate-500">{formatDate(a.data_inizio)} → {formatDate(a.data_fine)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Modifica auto" : "Nuova auto aziendale"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Modello</Label><Input data-testid="car-modello" value={form.modello} onChange={(e) => set("modello", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Targa</Label><Input data-testid="car-targa" value={form.targa} onChange={(e) => set("targa", e.target.value)} /></div>
            <div className="space-y-1.5 col-span-2"><Label>Parcheggio base</Label><Input data-testid="car-parcheggio" value={form.parcheggio_base} onChange={(e) => set("parcheggio_base", e.target.value)} /></div>
            <label className="flex items-center gap-2"><Checkbox data-testid="car-disp" checked={form.disponibile} onCheckedChange={(c) => set("disponibile", !!c)} /> Disponibile</label>
            <label className="flex items-center gap-2"><Checkbox data-testid="car-carb" checked={form.carta_carburante} onCheckedChange={(c) => set("carta_carburante", !!c)} /> Carta carburante</label>
            <label className="flex items-center gap-2"><Checkbox data-testid="car-tele" checked={form.telepass} onCheckedChange={(c) => set("telepass", !!c)} /> Telepass</label>
            <div className="space-y-1.5 col-span-2"><Label>Note</Label><Textarea data-testid="car-note" value={form.note || ""} onChange={(e) => set("note", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-car-btn" onClick={save} className="bg-[#E30613] hover:bg-[#C50510]">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
